import React from 'react'

type ReserveTaskStatus = 'new' | 'pending' | 'error'
type ReserveQueueTask = {
  no: string
  licensePlate: string
  entityId: string
  newReserve: number
  oldReserve: number
  status: ReserveTaskStatus
}

const defaultReserveQueueTask: ReserveQueueTask = {
  no: '',
  licensePlate: '',
  entityId: '',
  newReserve: 0,
  oldReserve: 0,
  status: 'new',
}

type ReserveSuccessfulTask = {
  no: ReserveQueueTask['no']
  reserve: ReserveQueueTask['newReserve']
}

type ReserveQueueContextProps = {
  queue: Array<ReserveQueueTask>
  addTask: (
    _no: ReserveQueueTask['no'],
    _licensePlate: ReserveQueueTask['licensePlate'],
    _entityId: ReserveQueueTask['entityId'],
    _newReserve: ReserveQueueTask['newReserve'],
    _oldReserve: ReserveQueueTask['oldReserve'],
  ) => void
  addTasks: (_arrayOfNewTasks: ReserveQueueTask[]) => void
  deleteTask: (_no: ReserveQueueTask['no']) => void
  onTaskSuccess: (_no: ReserveQueueTask['no']) => void
  changeTaskStatus: (_no: ReserveQueueTask['no'], _status: ReserveQueueTask['status']) => void
  getTask: (_no: ReserveQueueTask['no']) => ReserveQueueTask | null
  getLastSuccessfulTask: (_no: ReserveSuccessfulTask['no']) => ReserveSuccessfulTask | null
  hasTask: boolean
  successfulTasks: Array<ReserveSuccessfulTask>
  numberOfSuccess: number
}

const initialReserveQueueContext: ReserveQueueContextProps = {
  queue: [],
  addTask: (_no, _licensePlate, _newReserve, _oldReserve) => {},
  addTasks: (_arrayOfNewTasks) => {},
  deleteTask: (_no) => {},
  onTaskSuccess: (_no) => {},
  changeTaskStatus: (_no, _status) => {},
  getTask: (_no) => null,
  getLastSuccessfulTask: (_no) => null,
  hasTask: false,
  successfulTasks: [],
  numberOfSuccess: 0,
}

const ReserveQueueContext = React.createContext<ReserveQueueContextProps>(
  initialReserveQueueContext,
)

const ReserveQueueContextProvider = ({ children }: React.PropsWithChildren<unknown>) => {
  const [queue, setQueue] = React.useState<ReserveQueueTask[]>([])
  const [successfulTasks, setSuccessfulTasks] = React.useState<ReserveSuccessfulTask[]>([])

  const getTask = (no: ReserveQueueTask['no']) => {
    return queue.find((task) => task.no === no) || null
  }

  const getLastSuccessfulTask = (no: ReserveQueueTask['no']) => {
    return successfulTasks.find((task) => task.no === no) || null
  }

  const addTask = (
    no: string,
    licensePlate: string,
    entityId: string,
    newReserve: number,
    oldReserve: number,
  ) => {
    const newTask: ReserveQueueTask = {
      no,
      licensePlate,
      entityId,
      newReserve,
      oldReserve,
      status: 'new',
    }
    // To remove possibly existing task with same no and status 'error'
    const filteredQueue = queue.filter((task) => task.no !== no)

    setQueue([...filteredQueue, newTask])
  }
  const addTasks = (arrayOfNewTasks: ReserveQueueTask[]) => {
    const newQueue = [...queue, ...arrayOfNewTasks]
    setQueue(newQueue)
  }

  const deleteTask = (no: ReserveQueueTask['no']) => {
    const newQueue = queue.filter((task) => task.no !== no)
    setQueue(newQueue)
  }

  const onTaskSuccess = (no: ReserveQueueTask['no']) => {
    const currentTask = getTask(no) as ReserveQueueTask
    const successfulTask: ReserveSuccessfulTask = {
      no: currentTask.no,
      reserve: currentTask.newReserve,
    }
    setSuccessfulTasks([successfulTask, ...successfulTasks])

    deleteTask(no)
  }

  const changeTaskStatus = (no: ReserveQueueTask['no'], status: ReserveQueueTask['status']) => {
    const updatedQueue = queue.map((task) => {
      if (task.no !== no) return task
      return {
        ...task,
        status,
      } as ReserveQueueTask
    })
    setQueue(updatedQueue)
  }

  const contextValue = React.useMemo(
    () => ({
      queue,
      addTask,
      addTasks,
      deleteTask,
      onTaskSuccess,
      changeTaskStatus,
      getTask,
      getLastSuccessfulTask,
      hasTask: queue.length > 0,
      successfulTasks,
      numberOfSuccess: successfulTasks.length,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue, successfulTasks],
  )

  return (
    <ReserveQueueContext.Provider value={contextValue}>{children}</ReserveQueueContext.Provider>
  )
}

const useReserveQueue = () => React.useContext(ReserveQueueContext)

export { useReserveQueue, ReserveQueueContextProvider, defaultReserveQueueTask }
export type { ReserveQueueTask }
